import React, { useState } from 'react';
import {
  Box,
  Grid,
  Typography,
  Button,
  Card,
  CardContent,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
  Alert,
  Divider,
  CircularProgress
} from '@mui/material';
import {
  CreditCard as CreditCardIcon,
  AccountBalance as BankIcon,
  Euro as EuroIcon
} from '@mui/icons-material';

const PaymentForm = ({ reservationData, listing, onPayment, onBack }) => {
  const [paymentMethod, setPaymentMethod] = useState('stripe');
  const [paymentData, setPaymentData] = useState({
    cardNumber: '',
    expiryDate: '',
    cvv: '',
    cardHolderName: ''
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const handleInputChange = (field, value) => {
    setPaymentData(prev => ({
      ...prev,
      [field]: value
    }));

    // Nettoyer l'erreur pour ce champ
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const validatePaymentData = () => {
    const newErrors = {};

    if (paymentMethod === 'stripe') {
      if (!paymentData.cardNumber) {
        newErrors.cardNumber = 'Numéro de carte requis';
      } else if (!/^\d{16}$/.test(paymentData.cardNumber.replace(/\s/g, ''))) {
        newErrors.cardNumber = 'Numéro de carte invalide';
      }

      if (!paymentData.expiryDate) {
        newErrors.expiryDate = 'Date d\'expiration requise';
      } else if (!/^\d{2}\/\d{2}$/.test(paymentData.expiryDate)) {
        newErrors.expiryDate = 'Format invalide (MM/YY)';
      }

      if (!paymentData.cvv) {
        newErrors.cvv = 'CVV requis';
      } else if (!/^\d{3,4}$/.test(paymentData.cvv)) {
        newErrors.cvv = 'CVV invalide';
      }

      if (!paymentData.cardHolderName) {
        newErrors.cardHolderName = 'Nom du titulaire requis';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validatePaymentData()) {
      return;
    }

    setLoading(true);

    try {
      // Simuler le traitement du paiement
      await new Promise(resolve => setTimeout(resolve, 2000));

      const paymentInfo = {
        paymentMethod,
        paymentReference: `${paymentMethod}_${Date.now()}`,
        ...paymentData
      };

      await onPayment(paymentInfo);
    } catch (error) {
      setErrors({ payment: 'Erreur lors du traitement du paiement' });
    } finally {
      setLoading(false);
    }
  };

  const formatCardNumber = (value) => {
    const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
    const matches = v.match(/\d{4,16}/g);
    const match = matches && matches[0] || '';
    const parts = [];

    for (let i = 0, len = match.length; i < len; i += 4) {
      parts.push(match.substring(i, i + 4));
    }

    if (parts.length) {
      return parts.join(' ');
    } else {
      return v;
    }
  };

  const formatExpiryDate = (value) => {
    const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
    if (v.length >= 2) {
      return `${v.substring(0, 2)}/${v.substring(2, 4)}`;
    }
    return v;
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Typography variant="h6" gutterBottom>
        Paiement sécurisé
      </Typography>

      <Grid container spacing={3}>
        {/* Récapitulatif de la réservation */}
        <Grid item xs={12}>
          <Card sx={{ mb: 3, bgcolor: 'grey.50' }}>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom>
                Récapitulatif de votre réservation
              </Typography>
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">
                  {listing?.title}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">
                  Du {new Date(reservationData.startDate).toLocaleDateString('fr-FR')} au{' '}
                  {new Date(reservationData.endDate).toLocaleDateString('fr-FR')}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">
                  {reservationData.guestCount} invité{reservationData.guestCount > 1 ? 's' : ''}
                </Typography>
              </Box>

              <Divider sx={{ my: 1 }} />

              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="h6">
                  Total à payer
                </Typography>
                <Typography variant="h6" color="primary">
                  €{reservationData.totalAmount?.toFixed(2)}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Méthode de paiement */}
        <Grid item xs={12}>
          <FormControl component="fieldset">
            <FormLabel component="legend">
              <Typography variant="subtitle1">
                Méthode de paiement
              </Typography>
            </FormLabel>
            <RadioGroup
              value={paymentMethod}
              onChange={(e) => setPaymentMethod(e.target.value)}
              sx={{ mt: 1 }}
            >
              <FormControlLabel
                value="stripe"
                control={<Radio />}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <CreditCardIcon sx={{ mr: 1 }} />
                    Carte bancaire (Stripe)
                  </Box>
                }
              />
              <FormControlLabel
                value="bank_transfer"
                control={<Radio />}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <BankIcon sx={{ mr: 1 }} />
                    Virement bancaire
                  </Box>
                }
                disabled
              />
            </RadioGroup>
          </FormControl>
        </Grid>

        {/* Détails de paiement par carte */}
        {paymentMethod === 'stripe' && (
          <>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Numéro de carte"
                value={paymentData.cardNumber}
                onChange={(e) => handleInputChange('cardNumber', formatCardNumber(e.target.value))}
                error={!!errors.cardNumber}
                helperText={errors.cardNumber}
                placeholder="1234 5678 9012 3456"
                inputProps={{ maxLength: 19 }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Nom du titulaire"
                value={paymentData.cardHolderName}
                onChange={(e) => handleInputChange('cardHolderName', e.target.value)}
                error={!!errors.cardHolderName}
                helperText={errors.cardHolderName}
                placeholder="Jean Dupont"
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Date d'expiration"
                value={paymentData.expiryDate}
                onChange={(e) => handleInputChange('expiryDate', formatExpiryDate(e.target.value))}
                error={!!errors.expiryDate}
                helperText={errors.expiryDate}
                placeholder="MM/YY"
                inputProps={{ maxLength: 5 }}
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                fullWidth
                label="CVV"
                value={paymentData.cvv}
                onChange={(e) => handleInputChange('cvv', e.target.value.replace(/\D/g, ''))}
                error={!!errors.cvv}
                helperText={errors.cvv}
                placeholder="123"
                inputProps={{ maxLength: 4 }}
              />
            </Grid>
          </>
        )}

        {/* Message d'information pour virement */}
        {paymentMethod === 'bank_transfer' && (
          <Grid item xs={12}>
            <Alert severity="info">
              Les détails pour le virement bancaire vous seront envoyés par email après confirmation de votre réservation.
            </Alert>
          </Grid>
        )}

        {/* Erreur de paiement */}
        {errors.payment && (
          <Grid item xs={12}>
            <Alert severity="error">
              {errors.payment}
            </Alert>
          </Grid>
        )}

        {/* Sécurité */}
        <Grid item xs={12}>
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              🔒 Vos informations de paiement sont sécurisées et cryptées. 
              Nous ne stockons jamais vos données bancaires.
            </Typography>
          </Alert>
        </Grid>
      </Grid>

      {/* Boutons d'action */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
        <Button
          onClick={onBack}
          disabled={loading}
          sx={{ minWidth: 120 }}
        >
          Retour
        </Button>

        <Button
          type="submit"
          variant="contained"
          size="large"
          disabled={loading}
          sx={{ minWidth: 200 }}
          startIcon={loading ? <CircularProgress size={20} /> : <EuroIcon />}
        >
          {loading ? 'Traitement...' : `Payer €${reservationData.totalAmount?.toFixed(2)}`}
        </Button>
      </Box>
    </Box>
  );
};

export default PaymentForm;
